import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Settings, Brain, Wrench, ShieldCheck, Palette, Code,
  Upload, Mic,
} from 'lucide-react';
import styles from './WidgetConfigurator.module.css';
import WidgetHeader from '../../components/widgetHeader/WidgetHeader';
import LlmConfigTab from './LlmConfigTab/LlmConfigTab';
import ToolsConfigTab from './ToolsConfigTab/ToolsConfigTab';
import AuthConfigTab from './AuthConfigTab/AuthConfigTab';
import FileUploadConfigTab from './FileUploadConfigTab/FileUploadConfigTab';
import VoiceConfigTab from './VoiceConfigTab/VoiceConfigTab';
import { widgetsApi, settingsApi } from '../../../../lib/apiClient';
import { useAuthStore } from '../../../../store/authStore';
import { UserRole } from '../../../../enums/userRole';
import { toast } from '../../../../lib/toast';
import {
  DEFAULT_WIDGET_CONFIG,
  normalizeWidgetConfig,
  type WidgetConfig,
  type WidgetConnectorOption,
  type WidgetTokenUsage,
  WIDGET_LANGUAGE_DEFAULTS,
} from '../../shared/config';
import SettingsConfigTab from './SettingsConfigTab/SettingsConfigTab';
import StyleConfigTab from './StyleConfigTab/StyleConfigTab';
import EmbedConfigTab from './EmbedConfigTab/EmbedConfigTab';
import { hasMinRole } from '../../../../lib/permissions';
import { normalizeResponse } from '../../../../pages/settingsTabs/tokenSettingsTab/tokenSettingsUtils';
interface LogEntry {
  type: string;
  msg: string;
  detail?: unknown;
  ts: string;
}

function sanitizeVoiceDependencies(partial: Partial<WidgetConfig>, currentConfig: WidgetConfig): Partial<WidgetConfig> {
  const nextPartial = { ...partial };
  const nextVoiceEnabled = partial.voiceEnabled ?? currentConfig.voiceEnabled;

  if (!nextVoiceEnabled) {
    nextPartial.drivingModeEnabled = false;
  }

  return nextPartial;
}

export default function WidgetConfigurator() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const isPromptEditor = user?.role?.id === UserRole.PromptEditor;
  const canManage = hasMinRole(user?.role?.id, UserRole.WorkzoneManager);
  const canView = canManage || isPromptEditor;
  const isCreateMode = !id;

  const [widgetName, setWidgetName] = useState('New AI Agent');
  const [widgetActive, setWidgetActive] = useState(true);
  const [config, setConfig] = useState<WidgetConfig>({ ...DEFAULT_WIDGET_CONFIG });
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user || !canView) {
      navigate('/');
    }
  }, [user])

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const [activeStep, setActiveStep] = useState(1);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connectors, setConnectors] = useState<WidgetConnectorOption[]>([]);
  const [tokenUsage, setTokenUsage] = useState<WidgetTokenUsage | null>(null);

  // System-wide requirements checker switch - shown next to the widget's own switch (SuperAdmin only)
  const [checkerEnabledGlobally, setCheckerEnabledGlobally] = useState(true);
  useEffect(() => {
    if (!isSuperAdmin) return;
    settingsApi.getWebchatSettings()
      .then((raw: unknown) => {
        const setting = normalizeResponse(raw)['webchat_data_analyst_checker_enabled'];
        if (setting) setCheckerEnabledGlobally(String(setting.value === '' ? setting.default : setting.value) !== 'false');
      })
      .catch(() => { /* ignore - the switch still works, only the note is missing */ });
  }, [isSuperAdmin]);

  // Load connectors for S3 selector in upload config
  useEffect(() => {
    settingsApi.getConnectors()
      .then((data: any) => setConnectors(data.connectors || []))
      .catch(() => { /* ignore */ });
  }, []);

  // Load token usage for this widget
  useEffect(() => {
    if (!id) return;
    widgetsApi.getTokenUsage(id).then(setTokenUsage).catch(() => { /* ignore */ });
  }, [id]);

  const logEntriesRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLElement>(null);
  const origConsoleRef = useRef<Record<string, (...args: unknown[]) => void>>({});
  const origFetchRef = useRef<typeof fetch | null>(null);

  const serverUrl = import.meta.env.VITE_API_BASE_URL || '';

  // Load widget on mount (edit mode)
  useEffect(() => {
    if (isCreateMode) return;
    setLoading(true);
    widgetsApi.get(id!).then((widget: any) => {
      setWidgetName(widget.name || 'Untitled');
      setWidgetActive(widget.isActive ?? true);
      setConfig(normalizeWidgetConfig(widget.config));
      setDirty(false);
    }).catch((err: any) => {
      toast.error('Failed to load AI agent: ' + (err?.message || err));
      navigate('/widgets');
    }).finally(() => setLoading(false));
  }, [id]);

  const validateWidget = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!widgetName.trim()) errors.widgetName = 'AI agent name is required';
    if (!config.title.trim()) errors.title = 'Title is required';
    if (config.sessionTimeout < 1 || config.sessionTimeout > 1440) errors.sessionTimeout = 'Must be 1–1440 minutes';
    if (config.primaryColor && !/^#[0-9a-fA-F]{3,8}$/.test(config.primaryColor)) errors.primaryColor = 'Invalid hex color';
    return errors;
  };

  const handleSave = async () => {
    const errors = validateWidget();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix validation errors before saving');
      return;
    }
    setSaving(true);
    try {
      // Build save config: strip voice override fields when override is OFF
      const saveConfig = { ...sanitizeVoiceDependencies(config, config) };
      if (!saveConfig.voiceOverride) {
        delete (saveConfig as any).voiceTtsProvider;
        delete (saveConfig as any).voiceTtsModel;
        delete (saveConfig as any).voiceTtsVoice;
        delete (saveConfig as any).voiceApiKeyOpenai;
        delete (saveConfig as any).voiceGoogleApiKey;
        delete (saveConfig as any).voiceGoogleVoice;
        delete (saveConfig as any).voiceGoogleEnVoice;
        delete (saveConfig as any).voiceElevenLabsApiKey;
        delete (saveConfig as any).voiceElevenLabsVoiceId;
      }

      if (isCreateMode) {
        const created = await widgetsApi.create({ name: widgetName, config: saveConfig });
        toast.success('AI agent created');
        navigate(`/widgets/${created.id}`, { replace: true });
      } else {
        await widgetsApi.update(id!, { name: widgetName, isActive: widgetActive, config: saveConfig });
        toast.success('AI agent saved');
        setDirty(false);
        // Refresh widget preview so it picks up new allowedTools/dangerousTools
        (widgetRef.current as any)?.refreshConfig?.();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save AI agent');
    } finally {
      setSaving(false);
    }
  };

  const addLog = useCallback((type: string, msg: string, detail?: unknown) => {
    const now = new Date();
    const time = now.toLocaleTimeString('en-GB', { hour12: false });
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    setLogs(prev => [...prev, { type, msg, detail, ts: `${time}.${ms}` }]);
  }, []);

  // Intercept console & fetch on mount
  useEffect(() => {
    const orig = {
      log: console.log,
      debug: console.debug,
      warn: console.warn,
      error: console.error,
    };
    origConsoleRef.current = orig;

    const makeInterceptor = (level: string, logType: string) => {
      const origFn = orig[level as keyof typeof orig];
      return function (...args: unknown[]) {
        origFn.apply(console, args);
        const msg = args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
        if (msg.includes('[mcp-chat-widget]') || msg.includes('[MCP') || msg.includes('MCP ')) {
          const cleanMsg = msg.replace(/^\[mcp-chat-widget\]\s*/, '');
          addLog(logType, cleanMsg);
        }
      };
    };

    console.debug = makeInterceptor('debug', 'mcp') as typeof console.debug;
    console.log = makeInterceptor('log', 'info') as typeof console.log;
    console.warn = makeInterceptor('warn', 'warn') as typeof console.warn;
    console.error = makeInterceptor('error', 'error') as typeof console.error;

    const originalFetch = window.fetch;
    origFetchRef.current = originalFetch;
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      const url = typeof input === 'string' ? input : (input as Request).url;
      const method = init?.method || 'GET';
      if (url.includes('/mcp') || url.includes('/webchat')) {
        const shortUrl = url.replace(window.location.origin, '');
        let label = `${method} ${shortUrl}`;
        if (init?.body) {
          try {
            const body = JSON.parse(init.body as string);
            if (body.method) label += ` [${body.method}]`;
          } catch { /* ignore */ }
        }
        addLog('mcp', `-> ${label}`);
        try {
          const resp = await originalFetch.apply(window, [input, init]);
          addLog(resp.status >= 400 ? 'error' : 'mcp', `<- ${resp.status} ${shortUrl}`);
          return resp;
        } catch (err: any) {
          addLog('error', `<- FAIL ${shortUrl}: ${err.message}`);
          throw err;
        }
      }
      return originalFetch.apply(window, [input, init]);
    };

    addLog('info', 'Widget configurator loaded');

    return () => {
      console.log = orig.log;
      console.debug = orig.debug;
      console.warn = orig.warn;
      console.error = orig.error;
      if (origFetchRef.current) window.fetch = origFetchRef.current;
    };
  }, [addLog]);

  // Auto-scroll log
  useEffect(() => {
    const el = logEntriesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs]);



  const updateConfig = (partial: Partial<WidgetConfig>) => {
    setDirty(true);
    setConfig(prev => {
      const nextPartial = sanitizeVoiceDependencies(partial, prev);
      if (partial.language && partial.language !== prev.language) {
        const oldDefaults = WIDGET_LANGUAGE_DEFAULTS[prev.language];
        const newDefaults = WIDGET_LANGUAGE_DEFAULTS[partial.language];
        if (newDefaults) {
          if (!prev.greeting || prev.greeting === oldDefaults?.greeting) {
            nextPartial.greeting = newDefaults.greeting;
          }
          if (!prev.title || prev.title === oldDefaults?.title) {
            nextPartial.title = newDefaults.title;
          }
        }
      }
      return { ...prev, ...nextPartial };
    });
  };


  const handleWidgetNameChange = (name: string) => {
    setWidgetName(name);
    setDirty(true);
  };

  const handleToggleActive = () => {
    setWidgetActive(prev => !prev);
    setDirty(true);
  };

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/widgets');
  };

  // Redirect users who can't create/manage widgets away from create mode
  useEffect(() => {
    if (isCreateMode && !canManage) {
      navigate('/widgets');
    }
  }, [isCreateMode, canManage, navigate]);

  const ALL_TABS = [
    { id: 1, label: 'Settings', icon: Settings },
    { id: 2, label: 'System Prompt', icon: Brain },
    { id: 3, label: 'Tools', icon: Wrench },
    { id: 4, label: 'Auth', icon: ShieldCheck },
    { id: 6, label: 'Upload', icon: Upload },
    { id: 9, label: 'Voice', icon: Mic },
    { id: 5, label: 'Style', icon: Palette },
    { id: 7, label: 'Embed', icon: Code },
  ];

  // Admin users see only: Settings, Tools, Style, Embed, Event Log
  const ADMIN_TAB_IDS = [1, 2, 3, 5, 7, 8];
  // PromptEditor may only see/edit Settings + System Prompt
  const PROMPT_EDITOR_TAB_IDS = [1, 2];
  const TABS = isSuperAdmin
    ? ALL_TABS
    : isPromptEditor
      ? ALL_TABS.filter(t => PROMPT_EDITOR_TAB_IDS.includes(t.id))
      : ALL_TABS.filter(t => ADMIN_TAB_IDS.includes(t.id));

  if (loading) {
    return (
      <div className={styles.container}>
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '3rem' }}>Loading AI agent...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <WidgetHeader
        widgetName={widgetName}
        widgetActive={widgetActive}
        isCreateMode={isCreateMode}
        isSuperAdmin={isSuperAdmin}
        canToggleActive={canManage}
        id={id}
        dirty={dirty}
        saving={saving}
        onWidgetNameChange={handleWidgetNameChange}
        onToggleActive={handleToggleActive}
        onBack={handleBack}
        onSave={handleSave}
      />

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`${styles.tab} ${activeStep === t.id ? styles.tabActive : ''}`}
            onClick={() => setActiveStep(t.id)}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      <div className={`${styles.contentGrid}`}>
        <div className={styles.configPanel}>
          {/* Tab 1: Settings */}
          {activeStep === 1 && (
            <SettingsConfigTab
              config={config}
              onChange={updateConfig}
              tokenUsage={tokenUsage}
              isCreateMode={isCreateMode}
              isSuperAdmin={isSuperAdmin}
              checkerEnabledGlobally={checkerEnabledGlobally}
              updateConfig={updateConfig}
              validationErrors={validationErrors}
            />
          )}

          {/* Tab 2: System Prompt */}
          {activeStep === 2 && (
            <LlmConfigTab config={config} onChange={updateConfig} />
          )}

          {/* Tab 3: Tools */}
          {activeStep === 3 && (
            <ToolsConfigTab config={config} onChange={updateConfig} />
          )}

          {/* Tab 4: Auth */}
          {activeStep === 4 && (
            <AuthConfigTab config={config} onChange={updateConfig} />
          )}



          {/* Tab 6: Upload */}
          {activeStep === 6 && (
            <FileUploadConfigTab config={config} onChange={updateConfig} connectors={connectors} />
          )}

          {/* Tab 9: Voice */}
          {activeStep === 9 && (
            <VoiceConfigTab config={config} onChange={updateConfig} />
          )}

          {/* Tab 5: Style */}
          {activeStep === 5 && (
            <StyleConfigTab
              config={config}
              onChange={updateConfig}
              widgetRef={widgetRef}
              id={id}
              serverUrl={serverUrl}
              addLog={addLog}
            />
          )}
          
          {/* Tab 7: Embed */}
          {activeStep === 7 && (
            <EmbedConfigTab
              config={config}
              widgetRef={widgetRef}
              id={id}
              serverUrl={serverUrl}
              addLog={addLog} />
          )}
          {/* {activeStep === 8 && (<EventLogTab
              logs={logs}
              onClear={clearLog}
              onExport={exportLog}
            />
            )} */}
        </div>


      </div>


    </div>
  );
}

